import * as THREE from 'three';
import {
  PANEL_SIZE,
  BALL_RADIUS,
  WALL_HEIGHT,
  WALL_THICKNESS,
  HOLE_RADIUS,
  GOAL_RADIUS,
  CAMERA_DISTANCE,
  CAMERA_ANGLE_DEG,
  CAMERA_ANGLE_GYRO_DEG,
  CAMERA_FOV,
  CAMERA_FOLLOW_WEIGHT,
  CAMERA_FOLLOW_SMOOTH,
  CAMERA_ANGLE_SMOOTH,
  MAX_PIXEL_RATIO,
} from '../config.js';
import { wallSegmentBox } from './wallGeometry.js';

const WOOD_LIGHT = 0xc9a876;
const WOOD_DARK = 0x7a5a30;
const METAL = 0xcccccc;
const GOAL_GREEN = 0x22cc88;
const HOLE_BLACK = 0x000000;

export class SceneBuilder {
  constructor(canvas) {
    this.canvas = canvas;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1f1b2a);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    const aspect = (canvas.clientWidth || window.innerWidth) / (canvas.clientHeight || window.innerHeight);
    this.camera = new THREE.PerspectiveCamera(CAMERA_FOV, aspect, 0.1, 100);
    // 相机俯角：两套参数根据模式切换，运行时在 _camAngleRad 上平滑插值
    this._camAngleRad = (CAMERA_ANGLE_DEG * Math.PI) / 180;
    this._camTargetAngleRad = this._camAngleRad;
    this._camOffset = new THREE.Vector3();
    this._recomputeOffset();
    // 相机注视点（在 XZ 平面上移动）。初始为面板中心。
    this._camTarget = new THREE.Vector3(0, 0, 0);
    this.camera.position.copy(this._camOffset);
    this.camera.lookAt(this._camTarget);

    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 0.9);
    dir.position.set(5, 10, 6);
    dir.castShadow = true;
    dir.shadow.mapSize.set(1024, 1024);
    dir.shadow.camera.left = -10;
    dir.shadow.camera.right = 10;
    dir.shadow.camera.top = 10;
    dir.shadow.camera.bottom = -10;
    this.scene.add(dir);

    // 面板根节点（视觉倾斜在这里）
    this.panelGroup = new THREE.Group();
    this.scene.add(this.panelGroup);

    /** @type {THREE.Mesh|null} */
    this.ballMesh = null;
    /** @type {THREE.Mesh[]} */
    this.wallMeshes = [];
    /** @type {THREE.Mesh|null} */
    this.floorMesh = null;
    /** @type {THREE.Mesh[]} */
    this.holeMeshes = [];
    /** @type {THREE.Mesh|null} */
    this.goalMesh = null;

    this._resize();
    this._onResize = () => this._resize();
    window.addEventListener('resize', this._onResize);
  }

  _resize() {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h, false);
    if (this.camera) {
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    }
  }

  buildLevel(level) {
    this.clearLevel();
    const half = PANEL_SIZE / 2;

    // 面板
    const floorGeo = new THREE.BoxGeometry(PANEL_SIZE, 0.2, PANEL_SIZE);
    const floorMat = new THREE.MeshStandardMaterial({
      color: WOOD_LIGHT,
      roughness: 0.85,
      metalness: 0.05,
    });
    this.floorMesh = new THREE.Mesh(floorGeo, floorMat);
    this.floorMesh.position.y = -0.1;
    this.floorMesh.receiveShadow = true;
    this.panelGroup.add(this.floorMesh);

    // 外围墙
    const t = WALL_THICKNESS;
    const outer = [
      { cx: 0, cz: -half - t / 2, sx: PANEL_SIZE + 2 * t, sz: t },
      { cx: 0, cz: half + t / 2, sx: PANEL_SIZE + 2 * t, sz: t },
      { cx: -half - t / 2, cz: 0, sx: t, sz: PANEL_SIZE },
      { cx: half + t / 2, cz: 0, sx: t, sz: PANEL_SIZE },
    ];
    const wallMat = new THREE.MeshStandardMaterial({
      color: WOOD_DARK,
      roughness: 0.8,
      metalness: 0.05,
    });
    for (const w of outer) this._addWallMesh(w.cx, w.cz, w.sx, w.sz, wallMat);

    // 内部墙
    for (const wall of level.walls) {
      const box = wallSegmentBox(wall, PANEL_SIZE, t);
      this._addWallMesh(box.cx, box.cz, box.sx, box.sz, wallMat);
    }

    // 陷阱洞（黑色圆面，贴在面板上）
    const holeGeo = new THREE.CircleGeometry(HOLE_RADIUS, 32);
    const holeMat = new THREE.MeshBasicMaterial({ color: HOLE_BLACK });
    for (const [hx, hy] of level.holes) {
      const m = new THREE.Mesh(holeGeo, holeMat);
      m.rotation.x = -Math.PI / 2;
      m.position.set(hx - half, 0.001, half - hy);
      this.holeMeshes.push(m);
      this.panelGroup.add(m);
    }

    // 终点（绿色发光）
    const goalGeo = new THREE.CircleGeometry(GOAL_RADIUS, 32);
    const goalMat = new THREE.MeshStandardMaterial({
      color: GOAL_GREEN,
      emissive: GOAL_GREEN,
      emissiveIntensity: 0.8,
    });
    this.goalMesh = new THREE.Mesh(goalGeo, goalMat);
    this.goalMesh.rotation.x = -Math.PI / 2;
    this.goalMesh.position.set(level.goal[0] - half, 0.002, half - level.goal[1]);
    this.panelGroup.add(this.goalMesh);

    // 球（放进 panelGroup，跟随面板视觉倾斜；物理世界始终水平，所以物理坐标 = 面板局部坐标）
    const ballGeo = new THREE.SphereGeometry(BALL_RADIUS, 32, 32);
    const ballMat = new THREE.MeshStandardMaterial({
      color: METAL,
      metalness: 0.9,
      roughness: 0.2,
    });
    this.ballMesh = new THREE.Mesh(ballGeo, ballMat);
    this.ballMesh.castShadow = true;
    this.panelGroup.add(this.ballMesh);
    this.ballYOffset = 0;
  }

  _addWallMesh(cx, cz, sx, sz, mat) {
    const geo = new THREE.BoxGeometry(sx, WALL_HEIGHT, sz);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(cx, WALL_HEIGHT / 2, cz);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.wallMeshes.push(mesh);
    this.panelGroup.add(mesh);
  }

  setTilt(tiltX, tiltZ) {
    this.panelGroup.rotation.x = tiltX;
    this.panelGroup.rotation.z = tiltZ;
  }

  syncBall(ballBody) {
    if (!this.ballMesh || !ballBody) return;
    this.ballMesh.position.set(
      ballBody.position.x,
      ballBody.position.y + this.ballYOffset,
      ballBody.position.z
    );
    this.ballMesh.quaternion.set(
      ballBody.quaternion.x,
      ballBody.quaternion.y,
      ballBody.quaternion.z,
      ballBody.quaternion.w
    );
  }

  /**
   * 每帧更新相机水平位置，使其在"面板中心"与"球位置"之间按权重跟随。
   * 同时按模式平滑切换俯角（拖动模式 vs. 陀螺仪模式）。只平移 XZ 和改俯角，
   * 不动 FOV / 距离。用指数平滑避免抖动。
   *
   * ballBody.position 是水平世界坐标（cannon 世界不做倾斜），
   * 与面板 panelGroup 未倾斜时的局部坐标一致，直接当 XZ 目标使用即可。
   *
   * @param {import('cannon-es').Body|null} ballBody
   * @param {number} dt
   * @param {{ gyro?: boolean }} [opts]  gyro=true 时用更竖直的俯角
   */
  updateCamera(ballBody, dt, opts = {}) {
    const px = ballBody ? ballBody.position.x : 0;
    const pz = ballBody ? ballBody.position.z : 0;
    // 期望注视点：0 = 面板中心，px/pz = 球位置；按权重插值
    const desiredX = px * CAMERA_FOLLOW_WEIGHT;
    const desiredZ = pz * CAMERA_FOLLOW_WEIGHT;
    // 指数平滑（帧率无关），k = 1 - exp(-smooth*dt)
    const kPos = 1 - Math.exp(-CAMERA_FOLLOW_SMOOTH * Math.max(0, dt));
    this._camTarget.x += (desiredX - this._camTarget.x) * kPos;
    this._camTarget.z += (desiredZ - this._camTarget.z) * kPos;
    // 俯角目标随模式切换
    const targetDeg = opts.gyro ? CAMERA_ANGLE_GYRO_DEG : CAMERA_ANGLE_DEG;
    this._camTargetAngleRad = (targetDeg * Math.PI) / 180;
    const kAng = 1 - Math.exp(-CAMERA_ANGLE_SMOOTH * Math.max(0, dt));
    this._camAngleRad += (this._camTargetAngleRad - this._camAngleRad) * kAng;
    this._recomputeOffset();
    this.camera.position.set(
      this._camTarget.x + this._camOffset.x,
      this._camOffset.y,
      this._camTarget.z + this._camOffset.z
    );
    this.camera.lookAt(this._camTarget.x, 0, this._camTarget.z);
  }

  /** 关卡切换时把相机对准面板中心，避免从上一关位置渐进。 */
  resetCamera() {
    this._camTarget.set(0, 0, 0);
    // 俯角保持当前值，下一帧 updateCamera 会继续平滑到目标模式
    this._recomputeOffset();
    this.camera.position.copy(this._camOffset);
    this.camera.lookAt(0, 0, 0);
  }

  /** 根据当前 _camAngleRad 重新计算相机偏移（绕 X 轴的旋转）。 */
  _recomputeOffset() {
    this._camOffset.set(
      0,
      CAMERA_DISTANCE * Math.sin(this._camAngleRad),
      CAMERA_DISTANCE * Math.cos(this._camAngleRad)
    );
  }

  setBallYOffset(offset) {
    this.ballYOffset = offset;
  }

  setBallVisible(v) {
    if (this.ballMesh) this.ballMesh.visible = v;
  }

  setBallOpacity(opacity) {
    if (!this.ballMesh) return;
    this.ballMesh.material.transparent = opacity < 1;
    this.ballMesh.material.opacity = opacity;
  }

  clearLevel() {
    for (const m of this.wallMeshes) {
      this.panelGroup.remove(m);
      m.geometry.dispose();
    }
    this.wallMeshes = [];
    for (const m of this.holeMeshes) {
      this.panelGroup.remove(m);
      m.geometry.dispose();
    }
    this.holeMeshes = [];
    if (this.floorMesh) {
      this.panelGroup.remove(this.floorMesh);
      this.floorMesh.geometry.dispose();
      this.floorMesh = null;
    }
    if (this.goalMesh) {
      this.panelGroup.remove(this.goalMesh);
      this.goalMesh.geometry.dispose();
      this.goalMesh = null;
    }
    if (this.ballMesh) {
      this.panelGroup.remove(this.ballMesh);
      this.ballMesh.geometry.dispose();
      this.ballMesh = null;
    }
  }

  render() {
    this.renderer.render(this.scene, this.camera);
  }
}
